import type { ReactNode } from "react";
import { cn } from "@/shared/utils/cn";
import { EmptyState, type EmptyStateProps } from "./EmptyState";
import { ErrorState } from "./ErrorState";
import { LoadingSpinner } from "./LoadingSpinner";

interface ItemListProps<T extends { id: string }> {
  items: T[];
  renderItem: (item: T) => ReactNode;
  isLoading?: boolean;
  error?: Error | null;
  loadingText?: string;
  emptyState?: EmptyStateProps;
  divided?: boolean;
  className?: string;
}

export function ItemList<T extends { id: string }>({
  items,
  renderItem,
  isLoading = false,
  error,
  loadingText = "Loading...",
  emptyState = { message: "No items found." },
  divided = false,
  className,
}: ItemListProps<T>) {
  if (error) return <ErrorState message={error.message} />;
  if (isLoading) return <LoadingSpinner message={loadingText} />;
  if (items.length === 0) return <EmptyState {...emptyState} />;

  return (
    <ul className={cn("min-w-0", divided ? "divide-y divide-border overflow-hidden rounded-[20px] border border-border bg-card [&>li>div]:rounded-none [&>li>div]:border-0" : "grid gap-[9px]", className)}>
      {items.map((item) => <li key={item.id} className="min-w-0">{renderItem(item)}</li>)}
    </ul>
  );
}
